// AchievementsPage — Gamified badges + milestones
// Layout:
//   - Top nav: < Back | Achievements
//   - Hero: progress to next tier
//   - 4x2 grid of badges (locked = gray)
//   - Progress bars for each achievement

import { useNavigate } from 'react-router-dom';

interface Badge {
  id: string;
  icon: string;
  name: string;
  description: string;
  unlocked: boolean;
  progress?: number;     // 0..1
  progressLabel?: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

const BADGES: Badge[] = [
  { id: 'first-challenge', icon: '🎯', name: 'First Challenge',   description: 'Activate your first challenge',                  unlocked: true,  rarity: 'common' },
  { id: 'first-trade',      icon: '📈', name: 'First Trade',        description: 'Place your first trade',                          unlocked: true,  rarity: 'common' },
  { id: 'winning-streak',   icon: '🔥', name: 'On a Roll',          description: '5 winning trades in a row',                      unlocked: true,  rarity: 'rare' },
  { id: 'profit-target',    icon: '🎯', name: 'Halfway There',      description: 'Reach 50% of your profit target',                unlocked: true,  progress: 0.38, progressLabel: '38%', rarity: 'rare' },
  { id: 'bot-active',       icon: '🤖', name: 'AI Apprentice',      description: 'Run an AI bot for 7 days',                        unlocked: true,  progress: 0.85, progressLabel: '6/7 days', rarity: 'rare' },
  { id: 'funded',           icon: '💎', name: 'Funded Trader',      description: 'Pass your first challenge',                      unlocked: false, progress: 0.40, progressLabel: '40%', rarity: 'epic' },
  { id: 'withdraw',         icon: '💰', name: 'First Withdrawal',   description: 'Withdraw your first profit split',               unlocked: false, progress: 0,    progressLabel: 'Locked', rarity: 'rare' },
  { id: 'scaling',          icon: '📊', name: 'Scaling Up',         description: 'Hold 3 active funded accounts',                  unlocked: false, progress: 0.33, progressLabel: '1/3',  rarity: 'epic' },
  { id: 'bot-master',       icon: '🎯', name: 'Bot Master',         description: 'Run 3 AI bots simultaneously',                     unlocked: false, progress: 0.33, progressLabel: '1/3',  rarity: 'epic' },
  { id: 'million',          icon: '💰', name: 'Million Dollar',     description: 'Withdraw $1M in profit splits',                    unlocked: false, progress: 0,    progressLabel: 'Locked', rarity: 'legendary' },
  { id: 'referral',         icon: '🤝', name: 'Influencer',         description: 'Refer 10 active traders',                         unlocked: false, progress: 0.1,  progressLabel: '1/10',  rarity: 'rare' },
  { id: 'streak-30',        icon: '🌟', name: 'Streak Master',      description: '30-day profitable trading streak',               unlocked: false, progress: 0.10, progressLabel: '3/30',  rarity: 'legendary' },
];

const RARITY_COLOR: Record<Badge['rarity'], string> = {
  common: 'from-[#8E8E93] to-[#636366]',
  rare: 'from-[#007AFF] to-[#5856D6]',
  epic: 'from-[#FF9500] to-[#FF3B30]',
  legendary: 'from-[#FFD700] to-[#FF9500]',
};

export function AchievementsPage() {
  const nav = useNavigate();
  const unlocked = BADGES.filter((b) => b.unlocked).length;
  const total = BADGES.length;
  const nextBadge = BADGES.find((b) => !b.unlocked);
  const pct = (unlocked / total) * 100;

  return (
    <div className="min-h-screen bg-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Achievements</div>
        <div className="w-9" />
      </div>

      {/* Hero */}
      <div className="mx-4 mt-4 overflow-hidden rounded-[20px] bg-tf-text-1 p-5 text-white" style={{ background: 'linear-gradient(135deg, #1C1C1E 0%, #2A2A2A 100%)' }}>
        <div className="text-[12px] uppercase tracking-wide text-white/60">Your achievements</div>
        <div className="mt-1 flex items-baseline gap-2">
          <div className="text-[40px] font-bold tabular-nums tracking-tight">{unlocked}</div>
          <div className="text-[16px] text-white/60">/ {total}</div>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/15">
          <div className="h-full rounded-full bg-gradient-to-r from-[#34C759] to-[#007AFF]" style={{ width: pct + '%' }} />
        </div>
        {nextBadge && (
          <div className="mt-4 rounded-[12px] bg-white/10 p-3">
            <div className="text-[11px] uppercase tracking-wide text-white/60">Next badge</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-[20px]">{nextBadge.icon}</span>
              <div>
                <div className="text-[14px] font-semibold">{nextBadge.name}</div>
                <div className="text-[11px] text-white/70">{nextBadge.progressLabel}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Badges grid */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">All badges</h2>
      <div className="mx-4 grid grid-cols-3 gap-2">
        {BADGES.map((b) => (
          <div
            key={b.id}
            data-testid={`badge-${b.id}`}
            className={['rounded-[16px] border border-tf-border bg-white p-3 text-center', !b.unlocked ? 'opacity-50' : ''].join(' ')}
          >
            <div className={['mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br text-[26px]',
              b.unlocked ? RARITY_COLOR[b.rarity] : 'from-[#C7C7CC] to-[#8E8E93]'].join(' ')}>
              {b.unlocked ? b.icon : '🔒'}
            </div>
            <div className="mt-2 text-[11px] font-semibold text-tf-text-1">{b.name}</div>
            <div className="text-[10px] text-tf-text-3 line-clamp-2">{b.description}</div>
            {b.progress !== undefined && (
              <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-tf-surface-1">
                <div className="h-full rounded-full bg-tf-blue" style={{ width: (b.progress * 100) + '%' }} />
              </div>
            )}
            {b.progressLabel && <div className="mt-0.5 text-[9px] font-semibold text-tf-text-3">{b.progressLabel}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
